/**
 * The currency context object and its value type, with no auth dependency.
 *
 * Split out of CurrencyContext so a component that only READS the context
 * (`useOptionalCurrency`) does not pull Firebase in behind it — the provider
 * needs auth, a reader does not, and a rendering test should not need an API
 * key to import a table.
 */
import { createContext, useContext } from 'react';
import type { SupportedCurrency } from '../utils/formatters';

export interface CurrencyContextValue {
  /** User's preferred display currency (default: 'USD') */
  displayCurrency: SupportedCurrency;
  /** USD-based FX rates (e.g. { AUD: 1.58, GBP: 0.79 }) */
  rates: Record<string, number>;
  /** Convert a numeric amount from fromCurrency to the user's displayCurrency */
  convert: (amount: number, fromCurrency: string) => number;
  /** Format in the reader's display currency. `frozenRates` reads a stored
   *  report through the table it was computed against; omitted, the live
   *  table is used (item 17f). */
  formatInUserCurrency: (
    amount: number | null | undefined,
    fromCurrency: string,
    frozenRates?: Record<string, number> | null,
  ) => string;
  /** Whether rates are still loading */
  isLoading: boolean;
  /** Update the display currency preference */
  setDisplayCurrency: (currency: SupportedCurrency) => void;
}

export const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

/** The currency context, or null outside a provider — for components that can
 *  render without one (a test, a storyboard) and fall back to raw figures. */
export const useOptionalCurrency = (): CurrencyContextValue | null => useContext(CurrencyContext) ?? null;
